import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { CompanyType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { differenceBy } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Company, CompanyBasic } from '~hooks/useCompany/models';
import useManagedCompanies, {
  GetConnectedAccountsByCompanyIdParams,
} from '~hooks/useManagedCompanies/useManagedCompanies';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

interface ManageAccountsFormProps {
  companyId?: string;
  isOpen: boolean;
  managedCompanies?: Company[];
  onClose: (shouldRefetch?: true) => void;
}

export default function ManageAccountsForm({
  companyId,
  isOpen,
  managedCompanies,
  onClose,
}: ManageAccountsFormProps) {
  const formRef = useRef<FormHandler>(null);

  const { toasterStore } = useStores();
  const { createManagedCompany } = useManagedCompanies();

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);

  const companies = useMemo(() => {
    return (managedCompanies ?? []).map((company) => {
      return new CompanyBasic(
        company.id,
        company.legalName,
        company.companyType as CompanyType,
        company.treadId,
      );
    });
  }, [managedCompanies]);

  const handleSelectedCompaniesChange = useCallback(
    (selectedCompaniesDidChange: boolean) => {
      setIsSubmitButtonDisabled(!selectedCompaniesDidChange);
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (!companyId) {
      return;
    }

    formRef.current?.onSubmit(async (dto) => {
      try {
        setIsSaving(true);

        const toAdd = differenceBy(dto.companies, companies, (item) => item.id);

        const createRequests = toAdd.map((company) =>
          createManagedCompany({ companyId, managedCompanyId: company.id }),
        );

        await Promise.all(createRequests);

        toasterStore.push(
          alert(
            t('administration.managed_companies.manage_accounts_form.success_message'),
            AlertTypes.success,
          ),
        );
        onClose(true);
      } catch (error) {
        console.error(error);
      } finally {
        setIsSaving(false);
      }
    });
  }, [companyId, companies, onClose]);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [onClose, isSaving]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      preventCloseOnBackdropClick
      sx={{ '& .MuiDialog-paper': { overflowY: 'visible' } }}
    >
      <DialogHeader onClose={handleClose}>
        {t('administration.managed_companies.manage_accounts_form.title')}
      </DialogHeader>

      <DialogContent>
        {isOpen && companyId ? (
          <Form
            key={companyId}
            ref={formRef}
            companyId={companyId}
            managedCompanies={companies}
            onSelectedCompaniesChange={handleSelectedCompaniesChange}
          />
        ) : null}
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSaving}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={isSaving || isSubmitButtonDisabled}
          loading={isSaving}
          onClick={handleSubmit}
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

interface FormProps {
  companyId: string;
  managedCompanies: CompanyBasic[];
  onSelectedCompaniesChange: (selectedCompaniesDidChange: boolean) => void;
}

interface FormHandler {
  onSubmit: (callback: (args: FormDTO) => void) => void;
}

const formSchema = yup.object().shape({
  companies: yup.array().of(yup.mixed<CompanyBasic>().required()).required(),
});
type FormDTO = yup.InferType<typeof formSchema>;

const Form = forwardRef<FormHandler, FormProps>(function Form(
  { companyId, managedCompanies, onSelectedCompaniesChange },
  ref,
) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<FormDTO>({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { companies: [] },
  });

  const { getConnectedAccountsByCompanyId } = useManagedCompanies();

  const watchedCompanies = watch('companies');
  const managedCompanyIds = useMemo(() => {
    return new Set(managedCompanies.map((company) => company.id));
  }, [managedCompanies]);
  const selectedCompaniesDidChange = useMemo(() => {
    return watchedCompanies.length > 0;
  }, [watchedCompanies]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit(callback) {
        handleSubmit(callback)();
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    onSelectedCompaniesChange(selectedCompaniesDidChange);
  }, [onSelectedCompaniesChange, selectedCompaniesDidChange]);

  return (
    <Box>
      <AutocompleteAsyncFormField
        asyncCallback={(args: GetConnectedAccountsByCompanyIdParams) => {
          return getConnectedAccountsByCompanyId(args).then((response) => {
            const filteredConnectedCompanies = (response?.data ?? [])
              .map((account) => account.connectedCompany)
              .filter(
                (company): company is CompanyBasic =>
                  !!company && !managedCompanyIds.has(company.id),
              );

            return {
              pagination: response?.pagination,
              data: filteredConnectedCompanies,
            };
          });
        }}
        clearable={false}
        control={control}
        errors={errors}
        extraRequestOptions={{ companyId }}
        filterSelectedOptions={false}
        getLabel={(item: CompanyBasic | null) => item?.legalName ?? ''}
        getValue={(item: CompanyBasic | null) => item?.id ?? ''}
        hideLabel
        isRequired
        limitTags={2}
        multiple
        name="companies"
      />
    </Box>
  );
});
